import { AssignWorkerSection } from '@/components/features/WorkOrders/components/AssignWorkerSection';
import { CompanyPickerSheet, type PickableCompany } from '@/components/global/selectors/CompanyPickerSheet';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Label } from '@/components/ui/shadcn/label';
import { Separator } from '@/components/ui/shadcn/separator';
import { useTranslations } from '@/hooks/useTranslations';
import { cn, useCan } from '@/lib/utils';
import { type Company, type User, type WorkOrder } from '@/types';
import { Building2, Check, CheckCircle2, Edit, Pencil, Trash2, XCircle } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

interface WorkOrderSidebarProps {
    workOrder: WorkOrder;
    users: User[];
    companies?: Company[];
    onEdit?: () => void;
    onDelete?: () => void;
    onComplete?: () => void;
    onCancel?: () => void;
    onAssign?: (assignments: Array<{ userId: string; role: 'lead' | 'helper' }>) => void;
    onCompaniesChange?: (data: { company_id?: string; paying_company_id?: string | null }) => void;
    isReadOnly?: boolean;
    canDelete?: boolean;
    canAssignWorkers?: boolean;
    canEditCompanies?: boolean;
    canEditDescription?: boolean;
}

export function WorkOrderSidebar({
    workOrder,
    users = [],
    companies = [],
    onEdit,
    onDelete,
    onComplete,
    onCancel,
    onAssign,
    onCompaniesChange,
    canDelete = false,
    canAssignWorkers = false,
    canEditCompanies = false,
    canEditDescription = false,
}: WorkOrderSidebarProps) {
    const { t } = useTranslations();
    const { can } = useCan();
    const [isEditingAssignments, setIsEditingAssignments] = useState(false);
    const [isEditingCompanies, setIsEditingCompanies] = useState(false);
    const [companyId, setCompanyId] = useState(workOrder.company_id || '');
    const [payingCompanyId, setPayingCompanyId] = useState(workOrder.paying_company?.id || '');
    /** Which company field the picker sheet is open for. */
    const [companySheet, setCompanySheet] = useState<'service' | 'paying' | null>(null);
    // The names behind the ids being edited, so the fields read as values rather
    // than as ids while the sheet is the thing doing the picking.
    const [serviceCompany, setServiceCompany] = useState<PickableCompany | null>(
        workOrder.company ? { id: String(workOrder.company.id), name: workOrder.company.name ?? '' } : null,
    );
    const [payingCompany, setPayingCompany] = useState<PickableCompany | null>(
        workOrder.paying_company ? { id: String(workOrder.paying_company.id), name: workOrder.paying_company.name ?? '' } : null,
    );
    const assignSectionRef = useRef<{ save: () => void } | null>(null);
    const statusKey = (workOrder.status ?? '').toLowerCase();
    const isCompleted = statusKey === 'completed';
    const isCancelled = statusKey === 'canceled' || statusKey === 'cancelled';
    const canComplete = !isCompleted && !isCancelled;
    const canCancel = !isCompleted && !isCancelled;
    const canCompleteAction = can('work-orders.complete-work-order') && canComplete;
    const canCancelAction = can('work-orders.can-cancel-work-order') && canCancel;

    // Convert legacy assigned_user to assignments format for backward compatibility
    const assignmentsForDisplay = useMemo(() => {
        // If assignments exist, use them
        if (workOrder.assignments && workOrder.assignments.length > 0) {
            return workOrder.assignments;
        }

        // Otherwise, check for legacy assigned_user and convert it to assignment format
        if (workOrder.assigned_user) {
            return [
                {
                    id: `legacy-${workOrder.id}`,
                    work_order_id: workOrder.id,
                    user_id: workOrder.assigned_user.id,
                    user: workOrder.assigned_user,
                    role: 'lead' as const,
                    assigned_at: workOrder.created_at || new Date().toISOString(),
                    assigned_by: undefined,
                    created_at: workOrder.created_at || new Date().toISOString(),
                    updated_at: workOrder.updated_at || new Date().toISOString(),
                },
            ];
        }

        return [];
    }, [workOrder.assignments, workOrder.assigned_user, workOrder.id, workOrder.created_at, workOrder.updated_at]);

    // Sync company state when workOrder changes (but only when not editing to avoid overwriting user changes)
    useEffect(() => {
        if (!isEditingCompanies) {
            setCompanyId(workOrder.company_id ? String(workOrder.company_id) : '');
            setPayingCompanyId(workOrder.paying_company?.id ? String(workOrder.paying_company.id) : '');
            setServiceCompany(workOrder.company ? { id: String(workOrder.company.id), name: workOrder.company.name ?? '' } : null);
            setPayingCompany(
                workOrder.paying_company ? { id: String(workOrder.paying_company.id), name: workOrder.paying_company.name ?? '' } : null,
            );
        }
    }, [workOrder.company_id, workOrder.company, workOrder.paying_company, isEditingCompanies]);

    const handleSaveCompanies = () => {
        if (onCompaniesChange) {
            // Always send company_id if it's a valid non-empty string
            // Always send paying_company_id (can be empty string to clear it)
            const trimmedCompanyId = companyId?.trim();
            const trimmedPayingCompanyId = payingCompanyId?.trim();

            onCompaniesChange({
                company_id: trimmedCompanyId && trimmedCompanyId !== '' ? trimmedCompanyId : undefined,
                paying_company_id: trimmedPayingCompanyId && trimmedPayingCompanyId !== '' ? trimmedPayingCompanyId : null,
            });
            setIsEditingCompanies(false);
        }
    };

    const handleCancelCompanies = () => {
        setCompanyId(workOrder.company_id || '');
        setPayingCompanyId(workOrder.paying_company?.id || '');
        setServiceCompany(workOrder.company ? { id: String(workOrder.company.id), name: workOrder.company.name ?? '' } : null);
        setPayingCompany(workOrder.paying_company ? { id: String(workOrder.paying_company.id), name: workOrder.paying_company.name ?? '' } : null);
        setIsEditingCompanies(false);
    };

    const companySeeds = companies.map((company) => ({ id: String(company.id), name: company.name ?? '' }));

    return (
        <div className="space-y-6">
            <CompanyPickerSheet
                open={companySheet !== null}
                onOpenChange={(open) => setCompanySheet(open ? companySheet : null)}
                value={companySheet === 'paying' ? payingCompanyId || null : companyId || null}
                selected={companySheet === 'paying' ? payingCompany : serviceCompany}
                seedCompanies={companySeeds}
                allowClear={companySheet === 'paying'}
                title={
                    companySheet === 'paying'
                        ? t('work_orders.sidebar.paying_company', 'Paying Company')
                        : t('work_orders.sidebar.service_company', 'Service Company')
                }
                description={
                    companySheet === 'paying'
                        ? t('work_orders.sidebar.paying_company_hint', 'The company invoiced for this job.')
                        : t('work_orders.sidebar.service_company_hint', 'The company the service is carried out for.')
                }
                clearLabel={t('work_orders.sidebar.no_paying_company', 'No paying company')}
                onSelect={(company) => {
                    if (companySheet === 'paying') {
                        setPayingCompany(company);
                        setPayingCompanyId(company?.id ?? '');

                        return;
                    }

                    setServiceCompany(company);
                    setCompanyId(company?.id ?? '');
                }}
            />

            {/* Quick Actions */}
            <Card>
                <CardHeader>
                    <CardTitle>{t('work_orders.sidebar.quick_actions', 'Quick Actions')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                    <div className="flex flex-col gap-2">
                        {canCompleteAction && onComplete && (
                            <Button variant="default" className="bg-success hover:bg-success/90 text-success-foreground w-full" onClick={onComplete}>
                                <CheckCircle2 className="mr-2 h-4 w-4" />
                                {t('work_orders.sidebar.complete', 'Complete')}
                            </Button>
                        )}
                        {canCancelAction && onCancel && (
                            <Button variant="destructive" className="w-full" onClick={onCancel}>
                                <XCircle className="mr-2 h-4 w-4" />
                                {t('work_orders.sidebar.cancel', 'Cancel')}
                            </Button>
                        )}
                    </div>
                    <div className="flex flex-col gap-2">
                        {canEditDescription && onEdit && (
                            <Button variant="default" className="w-full" onClick={onEdit}>
                                <Edit className="mr-2 h-4 w-4" />
                                {t('work_orders.sidebar.edit', 'Edit')}
                            </Button>
                        )}

                        {canDelete && onDelete && (
                            <Button variant="outline" className="w-full" onClick={onDelete}>
                                <Trash2 className="mr-2 h-4 w-4" />
                                {t('work_orders.sidebar.delete', 'Delete')}
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Assigned Workers */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                    <CardTitle>{t('work_orders.sidebar.assigned_workers', 'Assigned Workers')}</CardTitle>
                    {canAssignWorkers && (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                if (isEditingAssignments) {
                                    // If in edit mode, trigger save via the ref
                                    if (assignSectionRef.current) {
                                        assignSectionRef.current.save();
                                    }
                                } else {
                                    // If not in edit mode, enter edit mode
                                    setIsEditingAssignments(true);
                                }
                            }}
                            className="h-8"
                        >
                            <Pencil className="mr-1.5 h-3.5 w-3.5" />
                            {isEditingAssignments
                                ? t('work_orders.sidebar.save', 'Save')
                                : assignmentsForDisplay.length > 0
                                  ? t('work_orders.sidebar.edit', 'Edit')
                                  : t('work_orders.sidebar.assigned_workers', 'Assign Workers')}
                        </Button>
                    )}
                </CardHeader>
                <CardContent>
                    <AssignWorkerSection
                        ref={assignSectionRef}
                        assignments={assignmentsForDisplay}
                        users={users}
                        onAssign={(assignments) => {
                            if (onAssign) {
                                onAssign(assignments);
                                setIsEditingAssignments(false);
                            }
                        }}
                        readOnly={!canAssignWorkers}
                        noTitle
                        isEditing={isEditingAssignments}
                        onEditingChange={setIsEditingAssignments}
                    />
                </CardContent>
            </Card>

            {/* Companies */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                    <CardTitle>{t('work_orders.sidebar.companies', 'Companies')}</CardTitle>
                    {canEditCompanies && (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                if (isEditingCompanies) {
                                    handleSaveCompanies();
                                } else {
                                    setIsEditingCompanies(true);
                                }
                            }}
                            className="h-8"
                        >
                            {isEditingCompanies ? (
                                <>
                                    <Check className="mr-1.5 h-3.5 w-3.5" />
                                    {t('work_orders.sidebar.save', 'Save')}
                                </>
                            ) : (
                                <>
                                    <Pencil className="mr-1.5 h-3.5 w-3.5" />
                                    {t('work_orders.sidebar.edit', 'Edit')}
                                </>
                            )}
                        </Button>
                    )}
                </CardHeader>
                <CardContent className="space-y-4">
                    {isEditingCompanies && canEditCompanies ? (
                        <>
                            <div className="space-y-2">
                                <Label htmlFor="service-company">{t('work_orders.sidebar.service_company', 'Service Company')}</Label>
                                <Button
                                    id="service-company"
                                    type="button"
                                    variant="outline"
                                    className="h-auto w-full justify-start gap-2 py-1.5 pl-1.5 font-normal"
                                    onClick={() => setCompanySheet('service')}
                                >
                                    <span
                                        className={cn(
                                            'text-muted-foreground flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
                                            serviceCompany ? 'bg-muted' : 'border-muted-foreground/40 border border-dashed',
                                        )}
                                    >
                                        <Building2 className="h-3.5 w-3.5" />
                                    </span>
                                    <span className={cn('min-w-0 truncate text-sm', !serviceCompany && 'text-muted-foreground')}>
                                        {serviceCompany?.name || t('work_orders.forms.create.placeholders.select_company', 'Select service company')}
                                    </span>
                                </Button>
                            </div>
                            <Separator />
                            <div className="space-y-2">
                                <Label htmlFor="billing-company">{t('work_orders.sidebar.paying_company', 'Paying Company')}</Label>
                                <Button
                                    id="billing-company"
                                    type="button"
                                    variant="outline"
                                    className="h-auto w-full justify-start gap-2 py-1.5 pl-1.5 font-normal"
                                    onClick={() => setCompanySheet('paying')}
                                >
                                    <span
                                        className={cn(
                                            'text-muted-foreground flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
                                            payingCompany ? 'bg-muted' : 'border-muted-foreground/40 border border-dashed',
                                        )}
                                    >
                                        <Building2 className="h-3.5 w-3.5" />
                                    </span>
                                    <span className={cn('min-w-0 truncate text-sm', !payingCompany && 'text-muted-foreground')}>
                                        {payingCompany?.name ||
                                            t('work_orders.forms.create.placeholders.select_paying_company', 'Select billing company')}
                                    </span>
                                </Button>
                            </div>
                            <div className="flex gap-2 pt-2">
                                <Button type="button" variant="outline" onClick={handleCancelCompanies} className="flex-1">
                                    {t('work_orders.sidebar.cancel_edit', 'Cancel')}
                                </Button>
                                <Button type="button" onClick={handleSaveCompanies} className="flex-1">
                                    <Check className="mr-2 h-4 w-4" />
                                    {t('work_orders.sidebar.save', 'Save')}
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="space-y-2">
                                <label className="text-muted-foreground text-sm font-medium">
                                    {t('work_orders.sidebar.service_company', 'Service Company')}
                                </label>
                                {workOrder.company ? (
                                    <div>
                                        <p className="font-medium">{workOrder.company.name}</p>
                                        {workOrder.company.email && <p className="text-muted-foreground mt-1 text-sm">{workOrder.company.email}</p>}
                                    </div>
                                ) : (
                                    <p className="text-muted-foreground text-sm italic">
                                        {t('work_orders.show.empty.no_company', 'No service company assigned')}
                                    </p>
                                )}
                            </div>
                            <Separator />
                            <div className="space-y-2">
                                <label className="text-muted-foreground text-sm font-medium">
                                    {t('work_orders.sidebar.paying_company', 'Paying Company')}
                                </label>
                                {workOrder.paying_company ? (
                                    <div>
                                        <p className="font-medium">{workOrder.paying_company.name}</p>
                                        {workOrder.paying_company.email && (
                                            <p className="text-muted-foreground mt-1 text-sm">{workOrder.paying_company.email}</p>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-muted-foreground text-sm italic">
                                        {t('work_orders.show.empty.no_company', 'No billing company assigned')}
                                    </p>
                                )}
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
